import { api, queryKeys, type User } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDate } from '../../lib/format';
import { Chip, Input } from '../../ui';

const col = createColumnHelper<User>();

export function MemberSearch() {
  // The search term lives in the URL (?q=), so a result set is a shareable link.
  const { query, setParam, setPage } = useTableQuery();
  const term = (query.q as string | undefined) ?? '';

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.members(query),
    queryFn: () => api.users.list(query),
    enabled: term.length > 0,
  });

  const columns = useMemo(
    () => [
      col.accessor('user_code', {
        header: 'User ID',
        // Opens the member's Achievement Reward tracker — see MemberList.tsx's
        // identical column for why (client requirement: any member ID click
        // opens the tracker). Row still links to the profile via 'View' there;
        // this list has no separate action column, so the ID is the one link.
        cell: (i) => <Link to={`/income/achievement-reward?member=${i.getValue()}`}>{i.getValue()}</Link>,
      }),
      col.accessor('name', { header: 'Name' }),
      col.accessor('email', { header: 'Email' }),
      col.accessor('phone', { header: 'Phone', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('status', { header: 'Status' }),
      col.accessor('created_at', { header: 'Joined', cell: (i) => formatDate(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Actions',
        // This list had no dedicated profile link before the ID column above
        // was repointed at the reward tracker — added here so "view profile"
        // isn't lost.
        cell: (info) => (
          <Chip as="a" tone="gold" href={`/members/view/${info.row.original.id}`}>
            View
          </Chip>
        ),
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Search Members" description="Search by User ID, name, email or phone" />
      <DataTable<User>
        title="Results"
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        emptyLabel={term ? 'No members matched that search' : 'Enter a search term'}
        onPageChange={setPage}
        toolbar={
          <Input
            placeholder="Search…"
            defaultValue={term}
            onChange={(e) => setParam('q', e.target.value)}
          />
        }
      />
    </>
  );
}
